/* eslint-disable no-case-declarations */
import React, { useEffect } from "react";
import get from "lodash/get";
import { Text } from "../../Atoms/StoryElements/Text";
import { Blurb } from "../../Atoms/StoryElements/Blurb";
import "./story-element-card.m.css";
import { Summary } from "../../Atoms/StoryElements/Summary";
import { BlockQuote } from "../../Atoms/StoryElements/BlockQuote";
import { BigFact } from "../../Atoms/StoryElements/BigFact";
import { AlsoRead } from "../../Atoms/StoryElements/AlsoRead";
import { Image } from "../../Atoms/StoryElements/Image";
import PropTypes from "prop-types";
import { QuestionAnswer } from "../../Atoms/StoryElements/QuestionAnswer";
import { ImageGallery } from "../../Atoms/StoryElements/imageGallery";
import { StoryElement } from "../../Atoms/StoryElements/StoryElement";
import { Video } from "../../Atoms/StoryElements/Video";
import { Reference } from "../../Atoms/StoryElements/Reference";
import { CaptionAttribution } from "../../Atoms/CaptionAttribution";
import { Attachment } from "../../Atoms/StoryElements/Attachment";
import { SocialShare, WithLazy, LazyLoadImages } from "@quintype/components";
import { SocialShareTemplate } from "../../Molecules/SocialShareTemplate";
import { facebookMobileVideoResizeFix, getTextColor } from "../../../utils/utils";
import { ImageSlideshow } from "../../Atoms/StoryElements/ImageSlideshow";
import Table from "../../../../components/story-templates/story-elements/Table";
import { ExpandIcon } from "../../../../components/atoms";
import { RightArrow } from "../../Svgs/right-arrow";
import { getCategoryAndSubCategory, getTemplateName } from "../../../../ga-utils";
import NewImageGallery from "../../../../components/molecules/NewImageGallery";
import { CtaText } from "../../../../components/story-templates/story-elements/CtaText";
import AdComponent from "../../../../components/molecules/AdComponent";

const getElementType = (element) => element.subtype || element.type || "";
const getElement = (story, element, config = {}, AdComponent, WidgetComp, index, cardId) => {
  const {
    summary = {},
    blurb = {},
    "also-read": alsoRead = {},
    "q-and-a": qaElement = {},
    question: questionElement = {},
    answer: answerElement = {},
    references = {},
  } = config;

  let elementType = getElementType(element);

  if (element.subtype === "table") {
    elementType = "table";
  }

  const storyElementId = get(element, ["id"]);

  switch (elementType) {
    case "table":
      return <Table element={element} />;
    case "ad":
      return AdComponent ? <AdComponent {...element} id={`ad-${index}_${cardId}`} index={index} /> : null;

    case "widget":
      return <WidgetComp widgetCode={element.config.customCode} id={`widget-${index}_${cardId}`} />;

    case "text":
      return <Text element={element} AdComponent={AdComponent} story={story} config={config} />;
    case "cta":
      return (
        <div styleName="cta-wrapper">
          <CtaText element={element} AdComponent={AdComponent} rightArrow={true} />
        </div>
      );
    case "summary":
      return null;

    case "blurb":
      const { template: blurbTemplate = "default", css: { borderColor = "#2f81cd" } = {} } = blurb;
      return <Blurb element={element} template={blurbTemplate} css={{ borderColor: borderColor }} />;
    case "quote":
    case "blockquote":
      return <BlockQuote element={element} />;

    case "bigfact":
      return <BigFact element={element} />;

    case "also-read":
      const { opts: { title = "" } = {} } = alsoRead;
      return <AlsoRead story={story} element={element} opts={{ title }} />;

    case "image":
      return <Image element={element} />;

    case "q-and-a":
      const {
        template: qaTemplate = "default",
        css: { iconColor = "" } = {},
        opts: { defaultIconType = "edge" } = {},
      } = qaElement;
      return (
        <QuestionAnswer
          element={element}
          opts={{
            type: "q-and-a",
            defaultIconType: defaultIconType,
          }}
          template={qaTemplate}
          css={{
            iconColor: iconColor,
          }}
        />
      );

    case "question":
      const {
        template: questionTemplate = "default",
        css: { questionIconColor = "" } = {},
        opts: { defaultQuestionIconType = "edge" } = {},
      } = questionElement;
      return (
        <QuestionAnswer
          element={element}
          opts={{
            type: "question",
            defaultIconType: defaultQuestionIconType,
          }}
          template={questionTemplate}
          css={{
            iconColor: questionIconColor,
          }}
        />
      );

    case "answer":
      const {
        template: answerTemplate = "default",
        css: { answerIconColor = "" } = {},
        opts: { defaultAnswerIconType = "edge" } = {},
      } = answerElement;
      return (
        <QuestionAnswer
          element={element}
          opts={{
            type: "answer",
            defaultIconType: defaultAnswerIconType,
          }}
          template={answerTemplate}
          css={{
            iconColor: answerIconColor,
          }}
        />
      );

    case "gallery":
      return (
        <div className="content-style" id={`image-gallery-${storyElementId}`}>
          <ImageGallery element={element} />
        </div>
      );

    case "image-gallery":
      return (
        <div className="content-style" id={`new-image-gallery-${storyElementId}`}>
          <NewImageGallery element={element} />
        </div>
      );

    case "slideshow":
      return (
        <div className="content-style" id={`image-slideshow-${storyElementId}`}>
          <ImageSlideshow element={element} />
        </div>
      );

    case "youtube-video":
    case "dailymotion-video":
      return (
        <WithLazy margin="100px">
          {() => (
            <div className="content-style" id={`video-${storyElementId}`}>
              <Video element={element} story={story} loadIframeOnClick={true} />
            </div>
          )}
        </WithLazy>
      );
    case "attachment":
      return <Attachment element={element} />;
    case "tweet":
    case "jsembed":
      return (
        <WithLazy margin="100px">
          {() => (
            <div className="content-style" id={`jsembed-${storyElementId}`}>
              <StoryElement element={element} />
            </div>
          )}
        </WithLazy>
      );
    case "references":
      const { opts: { showHeadline = true, headlineText = "" } = {} } = references;
      return (
        <Reference
          element={element}
          opts={{
            showHeadline: showHeadline,
            headlineText: headlineText,
          }}
        />
      );
    default:
      return (
        <div className="content-style" id={`element-${storyElementId}`}>
          <StoryElement element={element} story={story} />
        </div>
      );
  }
};

export const MainImageWrapper = ({
  imageElement,
  story,
  children,
  config,
  card,
  toggleSliderView,
  cardIndex,
  totalCount,
}) => {
  // Track photo expand button clicks for analytics
  const trackPhotoExpandClick = () => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const pageType = "story-page";
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType, config, story);
      window.dataLayer.push({
        event: "dl_story_interactions",
        event_action: "photo_expand_click",
        category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: getTemplateName(story?.["story-template"] || pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  // Handle expand button click with tracking
  const handleExpandClick = () => {
    // Track the expand action
    trackPhotoExpandClick();
    // Call the original toggle function
    toggleSliderView();
  };

  return (
    <>
      <div styleName="image-container" style={{ backgroundColor: config?.theme }} className="image-container">
        <Image element={imageElement} caption={false} aspectRatio={[[16, 9]]} />
        <div styleName="image-buttons">
          <div styleName="image-count">
            {cardIndex}/{totalCount}
          </div>
          <div styleName="image-share">
            <SocialShare
              template={SocialShareTemplate}
              title={story.headline}
              theme={config?.theme}
              fullUrl={encodeURI(`${get(story, ["url"], "")}#${card.id}`)}
              story={story}
            />
            <button onClick={handleExpandClick} styleName="expand-button">
              <ExpandIcon fill="#fff" />
            </button>
          </div>
        </div>
        <CaptionAttribution element={imageElement} config={config} />
      </div>
    </>
  );
};

MainImageWrapper.propTypes = {
  story: PropTypes.object,
  imageElement: PropTypes.object,
  config: PropTypes.object,
  children: PropTypes.node,
  isSliderView: PropTypes.bool,
  toggleSliderView: PropTypes.func,
  cardIndex: PropTypes.number,
  totalCount: PropTypes.number,
  card: PropTypes.shape({
    id: PropTypes.string,
  }),
};

export const StoryElementCard = ({
  story,
  card,
  heroVideoElementId = -1,
  config,
  isLive,
  theme,
  adComponent,
  widgetComp,
}) => {
  const textColor = getTextColor(theme);
  const isLiveBlog = isLive ? "live-blog" : "";
  let shouldRunFBMobileVideoFix = true;

  function loadAndProcessInstagramEmbeds() {
    if (typeof window !== "undefined" && !window.instgrm) {
      if (!document.querySelector("#instagram-embed-script")) {
        const script = document.createElement("script");
        script.setAttribute("id", "instagram-embed-script");
        script.setAttribute("src", "https://www.instagram.com/embed.js");
        script.async = true;
        script.onload = () => {
          window.instgrm?.Embeds?.process();
        };
        document.body.appendChild(script);
      }
    } else {
      // If already loaded, process embeds directly
      setTimeout(() => window.instgrm?.Embeds?.process(), 200);
    }
  }

  useEffect(() => loadAndProcessInstagramEmbeds(), [card]);

  return (
    <div className="arr--story-page-card-wrapper">
      {get(card, ["story-elements"], []).map((element, index) => {
        if (element.id === heroVideoElementId) return false;
        if (element.subtype && element.subtype === "facebook-post" && shouldRunFBMobileVideoFix) {
          facebookMobileVideoResizeFix();
          shouldRunFBMobileVideoFix = false;
        }
        if (element.subtype && element.subtype === "instagram") {
          loadAndProcessInstagramEmbeds();
        }
        const tableStyle = element && element.subtype === "table" ? "table" : "";
        const cardId = get(card, ["id"], "");
        return (
          <div
            key={element.id}
            className="arr--element-container"
            styleName={`element-container ${isLiveBlog} ${tableStyle} ${textColor}`}
          >
            {getElement(story, element, config, adComponent, widgetComp, index, cardId)}
          </div>
        );
      })}
    </div>
  );
};
StoryElementCard.propTypes = {
  story: PropTypes.object,
  card: PropTypes.array,
  heroVideoElementId: PropTypes.number,
  config: PropTypes.object,
  isLive: PropTypes.bool,
  theme: PropTypes.string,
  adComponent: PropTypes.func,
  widgetComp: PropTypes.func,
};

export const PhotoStoryElement = ({
  card = {},
  config,
  story,
  adComponent,
  widgetComp,
  toggleSliderView,
  cardIndex,
  totalCount,
  indexAd,
  isDesktop,
  firstCard,
}) => {
  let shouldRunFBMobileVideoFix = true;
  let firstImageRendered = false;
  return (
    <>
      <div styleName="story-card">
        {get(card, ["story-elements"], []).map((element, index) => {
          const cardId = get(card, ["id"], "");
          if (element.type === "image") {
            if (firstCard && !firstImageRendered) {
              const storyElements = get(card, ["story-elements"], []);
              const imageStoryElement = storyElements.find((element) => element?.type === "image");

              const firstImageS3 = get(imageStoryElement, ["image-s3-key"], "");
              firstImageRendered = true;
              return typeof window === "undefined" ? (
                <div styleName="ssr-image-container">
                  <figure styleName="ssr-figure">
                    <img
                      src={`https://${config?.["cdn-image"]}/${firstImageS3}?w=640&auto=format,compress&fit=max&q=70`}
                      srcSet={`https://${config?.["cdn-image"]}/${firstImageS3}?w=480&auto=format,compress&fit=max&q=70 480w, https://${config?.["cdn-image"]}/${firstImageS3}?w=640&auto=format,compress&fit=max&q=70 768w`}
                      sizes="(max-width: 767px) 480px, (min-width: 768px) 768px"
                      alt={story["hero-image-alt-text"] || story?.headline}
                      style={{ width: "100%" }}
                    />
                  </figure>
                </div>
              ) : (
                <MainImageWrapper
                  key={index}
                  imageElement={element}
                  story={story}
                  config={config}
                  card={card}
                  toggleSliderView={toggleSliderView}
                  cardIndex={cardIndex}
                  totalCount={totalCount}
                />
              );
            } else {
              return (
                <LazyLoadImages key={index}>
                  <MainImageWrapper
                    imageElement={element}
                    story={story}
                    config={config}
                    card={card}
                    toggleSliderView={toggleSliderView}
                    cardIndex={cardIndex}
                    totalCount={totalCount}
                  />
                </LazyLoadImages>
              );
            }
          } else {
            if (element.subtype && element.subtype === "facebook-post" && shouldRunFBMobileVideoFix) {
              facebookMobileVideoResizeFix();
              shouldRunFBMobileVideoFix = false;
            }
            return (
              <div styleName="element-container" key={index} className="photo-text-element">
                {getElement(story, element, config, adComponent, widgetComp, index, cardId)}
              </div>
            );
          }
        })}
      </div>
      {indexAd % 2 === 0 && <AdComponent isDesktop={isDesktop} story={story} adIndexNo={indexAd / 2} />}
    </>
  );
};

PhotoStoryElement.propTypes = {
  story: PropTypes.object,
  card: PropTypes.object,
  config: PropTypes.object,
  adComponent: PropTypes.func,
  widgetComp: PropTypes.func,
  toggleSliderView: PropTypes.func,
  cardIndex: PropTypes.number,
  totalCount: PropTypes.number,
  indexAd: PropTypes.number,
  isDesktop: PropTypes.bool,
  firstCard: PropTypes.bool,
};

export const SlotAfterStory = ({ id = "", element = {}, AdComponent, WidgetComp }) => {
  switch (element.type) {
    case "ad":
      return <AdComponent {...element} id={`ad-after-story-${id}`} />;
    case "widget":
      return <WidgetComp widgetCode={element.config.customCode} id={`widget-after-story-${id}`} />;
    default:
      return null;
  }
};

SlotAfterStory.propTypes = {
  id: PropTypes.string,
  element: PropTypes.object,
  AdComponent: PropTypes.func,
  WidgetComp: PropTypes.func,
};
